/**
 * Per-duel NPC answer skill: same match id → same "skill" for the whole game,
 * different matches → random strength. Each question still rolls `Math.random()`
 * against that fixed threshold.
 */
const PREFIX = "cwf:npc-skill:v1:";
/** ~1 in 4 right when unlucky — feels clearly weak. */
export const NPC_SKILL_CORRECT_MIN = 0.26;
/** ~9 in 10 right when hot — strong but not perfect. */
export const NPC_SKILL_CORRECT_MAX = 0.9;

function hashToUnit(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

/** Probability the CPU picks the correct choice on a given question (0–1). */
export function npcCorrectChanceForMatch(matchId: string): number {
  const u = hashToUnit(`${PREFIX}${matchId}`);
  return NPC_SKILL_CORRECT_MIN + u * (NPC_SKILL_CORRECT_MAX - NPC_SKILL_CORRECT_MIN);
}

/** Human leads by this many correct answers → CPU gets a temporary skill floor until the gap shrinks. */
export const NPC_CATCH_UP_CORRECT_GAP = 2;

/** While catching up, CPU is at least this likely to pick the right answer (still not guaranteed). */
export const NPC_CATCH_UP_CORRECT_FLOOR = 0.86;

/** Upper bound during catch-up so the bot never feels rigged-perfect. */
export const NPC_CATCH_UP_CORRECT_CAP = 0.93;

/**
 * After the CPU has this many consecutive correct answers, the **next** pick uses
 * half the usual correct probability (streak above this count → halve).
 * Example: streaks 0–2 use normal logic; once the CPU reaches streak 3 before a pick,
 * that pick's correct chance is halved until they miss (streak resets).
 */
export const NPC_STREAK_HALVE_AFTER_CONSECUTIVE = 2;

type TallyState = {
  answerCounts?: Record<string, { correct?: number } | undefined>;
  scores: Record<string, number>;
  answerStreaks?: Record<string, number>;
};

/** Prefer `answerCounts`; if missing (legacy state), approximate from score (+10 per correct). */
export function playerCorrectAnswerTally(
  state: TallyState,
  playerId: string,
): number {
  const n = state.answerCounts?.[playerId]?.correct;
  if (typeof n === "number") return n;
  return Math.floor((state.scores[playerId] ?? 0) / 10);
}

/**
 * Same baseline per match as {@link npcCorrectChanceForMatch}, unless the human is
 * ahead by {@link NPC_CATCH_UP_CORRECT_GAP} or more correct answers — then the CPU
 * plays stronger until the gap is under that threshold again. After
 * {@link NPC_STREAK_HALVE_AFTER_CONSECUTIVE} consecutive CPU corrects, the next pick's
 * chance is halved until the CPU misses.
 */
export function npcEffectiveCorrectChanceForCpuAnswer(
  matchId: string,
  state: TallyState,
  humanPlayerId: string,
  cpuPlayerId: string,
): number {
  const base = npcCorrectChanceForMatch(matchId);
  const humanC = playerCorrectAnswerTally(state, humanPlayerId);
  const cpuC = playerCorrectAnswerTally(state, cpuPlayerId);
  let chance =
    humanC - cpuC < NPC_CATCH_UP_CORRECT_GAP
      ? base
      : Math.min(
          NPC_CATCH_UP_CORRECT_CAP,
          Math.max(base, NPC_CATCH_UP_CORRECT_FLOOR),
        );

  const cpuStreak = state.answerStreaks?.[cpuPlayerId] ?? 0;
  if (cpuStreak > NPC_STREAK_HALVE_AFTER_CONSECUTIVE) {
    chance *= 0.5;
  }
  return Math.min(1, Math.max(0, chance));
}
